/*!
 * Broadcast Theme
 * custom.js 
 */

(() => {
  "use strict";

  /* -----------------------------
   * Helpers
   * ----------------------------- */
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const onReady = (fn) =>
    document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", fn, { once: true }) : fn();

  // Shopify theme editor will re-render sections; mark roots to avoid duplicate init
  const markInit = (el, key) => {
    if (!el) return false;
    const k = `__${key}Init`;
    if (el.dataset[k] === "true") return false;
    el.dataset[k] = "true";
    return true;
  };

 /* =========================================================
 * MTM: Tabs
 * ========================================================= */
const MTM_UI = (() => {
  function initTabs(scope = document) {
    $$("[data-mtm-tabs]", scope).forEach((card) => {
      if (!markInit(card, "tabs")) return;

      const tabs = $$("[data-mtm-tab]", card);
      const panels = $$("[data-mtm-panel]", card);
      if (!tabs.length || !panels.length) return;

      const activate = (index) => {
        const idx = String(index);

        tabs.forEach((t) => {
          const on = t.getAttribute("data-mtm-tab-index") === idx;
          t.classList.toggle("is-active", on);
          t.setAttribute("aria-selected", on ? "true" : "false");
        });

        panels.forEach((p) => {
          const on = p.getAttribute("data-mtm-tab-index") === idx;
          p.classList.toggle("is-active", on);
        });
      };

      const current =
        tabs.find((t) => t.classList.contains("is-active")) ||
        tabs.find((t) => t.getAttribute("aria-selected") === "true") ||
        tabs[0];

      activate(current.getAttribute("data-mtm-tab-index"));

      card.addEventListener("click", (e) => {
        const tab = e.target.closest("[data-mtm-tab]");
        if (!tab || !card.contains(tab)) return;
        e.preventDefault();
        activate(tab.getAttribute("data-mtm-tab-index"));
      });
    });
  }

  function initMobileTabs(scope = document) {
    $$("[data-mtm-mobile-tabs]", scope).forEach((mobileTabs) => {
      if (!markInit(mobileTabs, "mobileTabs")) return;

      const tabs = $$("[data-mtm-mobile-tab]", mobileTabs);
      const panels = $$("[data-mtm-mobile-panel]", mobileTabs);
      if (!tabs.length || !panels.length) return;

      const activate = (index) => {
        const idx = String(index);

        tabs.forEach((btn) => {
          const isActive = btn.getAttribute("data-tab-index") === idx;
          btn.classList.toggle("is-active", isActive);
          btn.setAttribute("aria-selected", isActive ? "true" : "false");
        });

        panels.forEach((panel) => {
          const isActive = panel.getAttribute("data-tab-index") === idx;
          panel.classList.toggle("is-active", isActive);
        });
      };

      const current =
        tabs.find((t) => t.classList.contains("is-active")) ||
        tabs.find((t) => t.getAttribute("aria-selected") === "true") ||
        tabs[0];

      activate(current.getAttribute("data-tab-index"));

      mobileTabs.addEventListener("click", (e) => {
        const tab = e.target.closest("[data-mtm-mobile-tab]");
        if (!tab || !mobileTabs.contains(tab)) return;
        e.preventDefault();
        activate(tab.getAttribute("data-tab-index"));
      });
    });
  }

  function init(scope = document) {
    initTabs(scope);
    initMobileTabs(scope);
  }

  return { init };
})();

  /* =========================================================
   * MTM: Blog view toggle (grid / linear)
   * ========================================================= */
  const MTM_BLOG_VIEW = (() => {
    const STORAGE_KEY = "mtm_blog_view";

    const safeGet = () => {
      try {
        return localStorage.getItem(STORAGE_KEY);
      } catch {
        return null;
      }
    };

    const safeSet = (v) => {
      try {
        localStorage.setItem(STORAGE_KEY, v);
      } catch {}
    };

    function setButtons(bar, view) {
      $$("[data-blog-view-btn]", bar).forEach((b) => {
        const isActive = b.getAttribute("data-blog-view-btn") === view;
        b.classList.toggle("is-active", isActive);
        b.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
    }

    function applyParity(section) {
      // Add .is-even / .is-odd for linear alternating (unlimited posts)
      $$(".grid.blog-listing .article.grid-item", section).forEach((el, idx) => {
        const n = idx + 1;
        el.classList.toggle("is-even", n % 2 === 0);
        el.classList.toggle("is-odd", n % 2 === 1);
      });
    }

    function setView(section, bar, view) {
      const v = view === "linear" ? "linear" : "grid";
      section.setAttribute("data-blog-view", v);
      setButtons(bar, v);
      applyParity(section);
      safeSet(v);
    }

    function init(root = document) {
      $$('.blog-section[data-section-type="blog"]', root).forEach((section) => {
        if (!markInit(section, "blogView")) return;

        const bar = section.querySelector("[data-blog-viewbar]");
        if (!bar) return;

        setView(section, bar, safeGet() || "grid");

        bar.addEventListener("click", (e) => {
          const btn = e.target.closest("[data-blog-view-btn]");
          if (!btn) return;
          setView(section, bar, btn.getAttribute("data-blog-view-btn"));
        });
      });
    }

    return { init };
  })();

  /* =========================================================
   * MTM: Anchor dropdown (scroll spy)
   * - delegation: 1 click handler
   * - per-dropdown IntersectionObserver (kept, but guarded)
   * ========================================================= */
  const MTM_ANCHOR_DROPDOWN = (() => {
    function init(root = document) {
      $$(".mtm-anchor-dropdown", root).forEach((dropdown) => {
        if (!markInit(dropdown, "anchorDropdown")) return;

        const button = dropdown.querySelector(".mtm-anchor-button");
        const currentEl = dropdown.querySelector("[data-anchor-current]");
        const menuLinks = $$(".mtm-anchor-menu a", dropdown);

        if (!button || !currentEl || !menuLinks.length) return;

        // Default to first label
        currentEl.textContent = (menuLinks[0].textContent || "").trim();
        button.setAttribute("aria-expanded", "false");

        const sectionIds = menuLinks
          .map((a) => (a.getAttribute("href") || "").replace("#", ""))
          .filter(Boolean);

        const sections = sectionIds.map((id) => document.getElementById(id)).filter(Boolean);
        if (!sections.length) return;

        const linkById = new Map(sectionIds.map((id, i) => [id, menuLinks[i]]));

        let activeId = sectionIds[0];

        const io = new IntersectionObserver(
          (entries) => {
            const visible = entries
              .filter((en) => en.isIntersecting)
              .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];

            if (!visible?.target?.id) return;

            const newId = visible.target.id;
            if (newId === activeId) return;

            activeId = newId;
            const link = linkById.get(activeId);
            if (link) currentEl.textContent = (link.textContent || "").trim();
          },
          {
            root: null,
            threshold: [0.2, 0.35, 0.5, 0.65],
          }
        );

        sections.forEach((s) => io.observe(s));

        // store ref for potential cleanup later (optional)
        dropdown.__mtmIO = io;
      });
    }

    // single delegated handler for all anchor dropdowns
    function bindGlobalHandlers() {
      document.addEventListener("click", (e) => {
        if (e.target.closest("[data-klaviyo-embed-dropdown]")) return;

        // Toggle open/close
        const toggle = e.target.closest(".mtm-anchor-button");
        if (toggle) {
          const dropdown = toggle.closest(".mtm-anchor-dropdown");
          if (!dropdown) return;

          e.preventDefault();
          const isOpen = dropdown.classList.toggle("is-open");
          toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
          return;
        }

        // Menu item click...
        const link = e.target.closest(".mtm-anchor-dropdown .mtm-anchor-menu a");
        if (link) {
          const dropdown = link.closest(".mtm-anchor-dropdown");
          const btn = dropdown?.querySelector(".mtm-anchor-button");
          const current = dropdown?.querySelector("[data-anchor-current]");
          if (!dropdown || !btn || !current) return;

          const href = link.getAttribute("href") || "";
          if (href.startsWith("#")) {
            e.preventDefault();
            const target = document.querySelector(href);
            if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
          }

          current.textContent = (link.textContent || "").trim();
          dropdown.classList.remove("is-open");
          btn.setAttribute("aria-expanded", "false");
          return;
        }

        // Outside click...
        if (!e.target.closest(".mtm-anchor-dropdown")) {
          $$(".mtm-anchor-dropdown.is-open").forEach((d) => {
            const btn = d.querySelector(".mtm-anchor-button");
            d.classList.remove("is-open");
            if (btn) btn.setAttribute("aria-expanded", "false");
          });
        }
      });
    }


    return { init, bindGlobalHandlers };
  })();

  /* =========================================================
   * MTM: Blog search filter
   * ========================================================= */
  const MTM_BLOG_SEARCH = (() => {
    function init(root = document) {
      $$(".blog-section", root).forEach((section) => {
        if (!markInit(section, "blogSearch")) return;

        const input = section.querySelector("[data-blog-search]");
        if (!input) return;

        const getCards = () =>
          Array.from(
            section.querySelectorAll(
              "article, .blog-item, .blog__post, .grid__item, [data-article], [data-article-card]"
            )
          );

        const getTitle = (card) => {
          const el =
            card.querySelector(".article__title") ||
            card.querySelector(".blog__title") ||
            card.querySelector("h1,h2,h3,h4,h5,h6") ||
            card.querySelector("a");
          return el ? (el.textContent || "").trim().toLowerCase() : "";
        };

        const apply = () => {
          const q = (input.value || "").trim().toLowerCase();
          const cards = getCards();

          if (!q) {
            cards.forEach((card) => card.classList.remove("mtm-blog__search-hidden"));
            return;
          }

          cards.forEach((card) => {
            const match = getTitle(card).includes(q);
            card.classList.toggle("mtm-blog__search-hidden", !match);
          });
        };

        input.addEventListener("input", apply, { passive: true });
        input.addEventListener("search", apply, { passive: true });
      });
    }

    return { init };
  })();

  /* =========================================================
   * MTM: Klaviyo embed dropdown (tabs inside dropdown)
   * ========================================================= */
  const MTM_KLAVIYO_DROPDOWN = (() => {
    function closeAll(exceptRoot = null) {
      $$('[data-klaviyo-embed-dropdown] .mtm-anchor-dropdown.is-open').forEach((dd) => {
        const root = dd.closest("[data-klaviyo-embed-dropdown]");
        if (exceptRoot && root === exceptRoot) return;
        const btn = dd.querySelector(".mtm-anchor-button");
        dd.classList.remove("is-open");
        if (btn) btn.setAttribute("aria-expanded", "false");
      });
    }

    function onDocClick(e) {
      const root = e.target.closest("[data-klaviyo-embed-dropdown]");
      const clickedToggle = e.target.closest(".mtm-anchor-button");
      const clickedItem = e.target.closest("button[data-klaviyo-tab]");

      // outside click closes all
      if (!root) {
        closeAll(null);
        return;
      }

      const dropdown = root.querySelector(".mtm-anchor-dropdown");
      const toggleBtn = root.querySelector(".mtm-anchor-button");
      const current = root.querySelector("[data-anchor-current]");
      const panelWrap = document.querySelector(`[data-klaviyo-panels="${root.id}"]`);

      if (!dropdown || !toggleBtn || !panelWrap) return;

      const openMenu = () => {
        toggleBtn.setAttribute("aria-expanded", "true");
        dropdown.classList.add("is-open");
      };

      const closeMenu = () => {
        toggleBtn.setAttribute("aria-expanded", "false");
        dropdown.classList.remove("is-open");
      };

      const setActive = (tabId, label) => {
        $$("[data-klaviyo-panel]", panelWrap).forEach((p) => {
          const active = p.getAttribute("data-klaviyo-panel") === tabId;
          p.classList.toggle("is-active", active);
        });
        if (current && label) current.textContent = label;
      };

      if (clickedToggle && root.contains(clickedToggle)) {
        e.preventDefault();
        const isOpen = toggleBtn.getAttribute("aria-expanded") === "true";
        if (isOpen) closeMenu();
        else {
          closeAll(root); // only one open at a time
          openMenu();
        }
        return;
      }

      if (clickedItem && root.contains(clickedItem)) {
        e.preventDefault();
        const tabId = clickedItem.getAttribute("data-klaviyo-tab");
        const label = (clickedItem.textContent || "").trim();
        setActive(tabId, label);
        closeMenu();
      }
    }

    function onKeyDown(e) {
      if (e.key !== "Escape") return;
      closeAll(null);
    }

    function bind() {
      document.addEventListener("click", onDocClick, true);
      document.addEventListener("keydown", onKeyDown);
    }

    return { bind };
  })();

  /* =========================================================
   * MTM: Klaviyo popup
   * ========================================================= */
  const MTM_KLAVIYO_POPUP = (() => {
    function init() {
      const popup = document.getElementById("klaviyoPopup");
      if (!popup || !markInit(popup, "klaviyoPopup")) return;

      const closeBtn = popup.querySelector(".mtm-popup-close");

      const openPopup = () => {
        popup.classList.add("is-active");
        popup.setAttribute("aria-hidden", "false");
        document.body.style.overflow = "hidden";
      };

      const closePopup = () => {
        popup.classList.remove("is-active");
        popup.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
        history.replaceState(null, "", window.location.pathname + window.location.search);
      };

      document.addEventListener("click", (e) => {
        const link = e.target.closest('a[href="#open-klaviyo-popup"]');
        if (!link) return;
        e.preventDefault();
        openPopup();
      });

      if (closeBtn) closeBtn.addEventListener("click", closePopup);

      popup.addEventListener("click", (e) => {
        if (e.target === popup) closePopup();
      });

      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && popup.classList.contains("is-active")) closePopup();
      });

      if (window.location.hash === "#open-klaviyo-popup") openPopup();
    }

    return { init };
  })();

/* =========================================================
 * MTM: Orbit carousel 
 * ========================================================= */
  const MTM_ORBIT = (() => {
    const GAP_PX = 40;
    const MAX_VISIBLE_STEPS = 3;
    const isMobile = () => window.matchMedia("(max-width: 767px)").matches;
    const isDesktop = () => !isMobile();

    const rafThrottle = (fn) => {
      let raf = 0;
      return (...args) => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          fn(...args);
        });
      };
    };

    function init(root = document) {
      $$("[data-orbit-carousel]", root).forEach((carousel) => {
        if (!markInit(carousel, "orbit")) return;

        // --- MTM: clone slides 3x (do not change anything else) ---
        if (carousel.dataset.cloned3x !== "true") {
          const originals = Array.from(carousel.querySelectorAll(".mtm-orbit__item"));
          if (originals.length) {
            const frag = document.createDocumentFragment();

            // append 2 extra sets (originals already exist) => total = 3x
            for (let r = 0; r < 2; r++) {
              originals.forEach((node) => {
                frag.appendChild(node.cloneNode(true));
              });
            }

            carousel.appendChild(frag);

            // update total to match new count (keeps your logic consistent)
            carousel.dataset.total = String(originals.length * 3);
            carousel.dataset.cloned3x = "true";
          }
        }
        // --- end clone ---

        const items = Array.from(carousel.querySelectorAll(".mtm-orbit__item"));
        const total = items.length;
        if (!items.length || total <= 0) return;

        const prevBtn = carousel.querySelector("[data-orbit-prev]");
        const nextBtn = carousel.querySelector("[data-orbit-next]");

        let current = 0;

        // cached “true” card width (measured WITHOUT transforms)
        let baseCardW = 0;

        const wrappedDelta = (from, to, n) => {
          let d = to - from;
          const half = n / 2;
          if (d > half) d -= n;
          if (d < -half) d += n;
          return d;
        };

        const measureCardWidth = () => {
          const cssCardW =
            parseFloat(getComputedStyle(carousel).getPropertyValue("--card-width")) || 560;

          const el = items[0];
          let measuredW = 0;

          if (el) {
            const prevTransform = el.style.transform;
            const prevTransition = el.style.transition;

            // measure without animation + without scale
            el.style.transition = "none";
            el.style.transform = "none";
            measuredW = el.getBoundingClientRect().width;

            // restore
            el.style.transform = prevTransform;
            el.style.transition = prevTransition;
          }

          baseCardW = Math.max(measuredW, cssCardW, 520);
        };

        const getCardWidth = () => baseCardW || 560;

        const update = () => {
          const cardW = getCardWidth();
          let xStep = cardW + GAP_PX;

          // MOBILE ONLY: cap the step so the next/prev card always peeks
          if (isMobile()) {
            const maxStep = Math.round(window.innerWidth * 0.65);
            xStep = Math.min(xStep, maxStep);
          }

          const tiltDeg = 2;
          const yDropPx = 10;

          items.forEach((el, idx) => {
            const d = wrappedDelta(current, idx, total);

            if (Math.abs(d) > MAX_VISIBLE_STEPS) {
              el.style.opacity = "0";
              el.setAttribute("aria-hidden", "true");
              el.style.pointerEvents = "none";
              el.dataset.step = "x";
              return;
            }

            const t = 1 - Math.min(Math.abs(d) / (MAX_VISIBLE_STEPS + 0.25), 1);

            const x = d * xStep;
            const n = Math.abs(d) / MAX_VISIBLE_STEPS;
            const y = yDropPx * (n * n);

            const scale = 0.9 + 0.12 * t;
            const z = Math.round(10 + 40 * t);
            const rot = d === 0 ? 0 : d < 0 ? -tiltDeg : tiltDeg;

            el.style.opacity = Math.abs(d) === MAX_VISIBLE_STEPS ? "0.18" : "1";
            el.setAttribute("aria-hidden", "false");
            el.style.pointerEvents = "";
            el.dataset.step = String(d);

            el.style.transform =
              `translate(-50%, -50%) ` +
              `translateX(${x}px) ` +
              `translateY(${y}px) ` +
              `translateZ(${z}px) ` +
              `rotate(${rot}deg) ` +
              `scale(${scale})`;

            el.style.zIndex = String(100 + Math.round(100 * t));
            el.style.cursor = d === 0 ? "default" : "pointer";
          });
        };

        // measure once up front (before first paint)
        measureCardWidth();

        const updateRaf = rafThrottle(() => {
          measureCardWidth();
          update();
        });

        const go = (delta) => {
          current = (current + delta + total) % total;
          update();
        };

        if (prevBtn) prevBtn.addEventListener("click", () => go(-1));
        if (nextBtn) nextBtn.addEventListener("click", () => go(1));

        // click any non-center card to advance (kept)
        carousel.addEventListener("click", (e) => {
          // don’t treat a drag as a click
          if (carousel.dataset.dragging === "true") return;

          const item = e.target.closest(".mtm-orbit__item");
          if (!item) return;

          const d = parseInt(item.dataset.step, 10);
          if (!Number.isFinite(d) || d === 0) return;

          if (d < 0) go(-1);
          if (d > 0) go(1);
        });

        // touch swipe (kept) — horizontal only
        let startX = 0, startY = 0;
        carousel.addEventListener(
          "touchstart",
          (e) => {
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
          },
          { passive: true }
        );

        carousel.addEventListener(
          "touchend",
          (e) => {
            const endX = e.changedTouches[0].clientX;
            const endY = e.changedTouches[0].clientY;
            const diffX = startX - endX;
            const diffY = startY - endY;

            if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
              go(diffX > 0 ? 1 : -1);
            }
          },
          { passive: true }
        );

        // =========================================================
        // DESKTOP: trackpad horizontal scroll ONLY
        // - ignore vertical scroll
        // - ignore mouse wheel / line-mode wheels
        // - ignore pinch-to-zoom (ctrlKey on Mac trackpads)
        // =========================================================
        let wheelLock = false;
        let wheelAccumX = 0;

        const wheelUnlock = () => {
          wheelLock = false;
          wheelAccumX = 0;
        };

        const onWheel = (e) => {
          if (!isDesktop()) return;

          // Trackpad typically uses pixel deltas (deltaMode === 0).
          // Mouse wheels often report lines/pages (1/2) — ignore those.
          if (e.deltaMode !== 0) return;

          // On Mac, ctrlKey is commonly true during pinch-to-zoom.
          if (e.ctrlKey) return;

          const dx = e.deltaX || 0;
          const dy = e.deltaY || 0;

          // must be clearly horizontal intent
          if (Math.abs(dx) <= Math.abs(dy)) return;
          if (Math.abs(dx) < 2) return; // tiny jitter guard

          e.preventDefault();

          wheelAccumX += dx;

          const THRESH = 60;
          if (!wheelLock && Math.abs(wheelAccumX) >= THRESH) {
            wheelLock = true;
            go(wheelAccumX > 0 ? 1 : -1);
            setTimeout(wheelUnlock, 220);
          }
        };

        carousel.addEventListener("wheel", onWheel, { passive: false });

        // =========================================================
        // DRAG (mouse + pointer)
        // MOBILE ONLY now (desktop must be trackpad + click only)
        // =========================================================
        if (isMobile()) {
          const DRAG_THRESHOLD_PX = 10; // when we consider it a drag (to block click)
          const STEP_THRESHOLD_PX = 60; // distance to advance 1 step

          let isDown = false;
          let dragStartX = 0;
          let dragStartY = 0;
          let lastX = 0;
          let accumX = 0;

          const setDragging = (v) => {
            carousel.dataset.dragging = v ? "true" : "false";
            if (v) {
              carousel.style.userSelect = "none";
              carousel.style.webkitUserSelect = "none";
              carousel.style.cursor = "grabbing";
            } else {
              carousel.style.userSelect = "";
              carousel.style.webkitUserSelect = "";
              carousel.style.cursor = "";
            }
          };

          const onPointerDown = (e) => {
            // only primary button
            if (e.pointerType === "mouse" && e.button !== 0) return;

            isDown = true;
            dragStartX = e.clientX;
            dragStartY = e.clientY;
            lastX = e.clientX;
            accumX = 0;

            setDragging(false);

            try {
              carousel.setPointerCapture(e.pointerId);
            } catch (_) {}
          };

          const onPointerMove = (e) => {
            if (!isDown) return;

            const dx = e.clientX - lastX;
            lastX = e.clientX;
            accumX += dx;

            const totalDX = e.clientX - dragStartX;
            const totalDY = e.clientY - dragStartY;

            if (Math.abs(totalDX) > Math.abs(totalDY) && Math.abs(totalDX) > DRAG_THRESHOLD_PX) {
              if (carousel.dataset.dragging !== "true") setDragging(true);
              e.preventDefault();
            } else {
              return;
            }

            while (accumX <= -STEP_THRESHOLD_PX) {
              accumX += STEP_THRESHOLD_PX;
              go(1);
            }
            while (accumX >= STEP_THRESHOLD_PX) {
              accumX -= STEP_THRESHOLD_PX;
              go(-1);
            }
          };

          const onPointerUp = (e) => {
            if (!isDown) return;
            isDown = false;

            const wasDragging = carousel.dataset.dragging === "true";

            setDragging(false);

            try {
              carousel.releasePointerCapture(e.pointerId);
            } catch (_) {}

            if (wasDragging) {
              carousel.dataset.dragging = "true";
              setTimeout(() => (carousel.dataset.dragging = "false"), 0);
            }
          };

          carousel.addEventListener("pointerdown", onPointerDown, { passive: true });
          carousel.addEventListener("pointermove", onPointerMove, { passive: false });
          carousel.addEventListener("pointerup", onPointerUp, { passive: true });
          carousel.addEventListener("pointercancel", onPointerUp, { passive: true });
          carousel.addEventListener("pointerleave", onPointerUp, { passive: true });
        } else {
          // ensure click works normally on desktop
          carousel.dataset.dragging = "false";
        }

        // keyboard (kept)
        carousel.addEventListener("keydown", (e) => {
          if (e.key === "ArrowLeft") go(-1);
          if (e.key === "ArrowRight") go(1);
        });

        window.addEventListener("resize", updateRaf, { passive: true });

        update();

        window.addEventListener("load", updateRaf, { once: true, passive: true });
        setTimeout(updateRaf, 150);
      });
    }

    return { init };
  })();
  /* =========================================================
   * header drawer
   * ========================================================= */
document.addEventListener("click", (e) => {
  const closeBtn = e.target.closest(".header__mobile__button .drawer__close");
  if (!closeBtn) return;

  // Find the hamburger toggle and trigger it
  const toggle = closeBtn
    .closest(".header__mobile__button")
    ?.querySelector('[data-drawer-toggle="hamburger"]');

  if (toggle) {
    e.preventDefault();
    toggle.click();
  }
});

document.addEventListener("DOMContentLoaded", function () {
  if (window.innerWidth < 750) {
    document.querySelectorAll('details.accordion[open]').forEach(function(el) {
      el.removeAttribute('open');
    });
  }
});
  /* =========================================================
   * Boot 
   * ========================================================= */
  function boot(root = document) {
    MTM_UI.init(root);
    MTM_BLOG_VIEW.init(root);
    MTM_ANCHOR_DROPDOWN.init(root);
    MTM_BLOG_SEARCH.init(root);
    MTM_KLAVIYO_POPUP.init();
    MTM_ORBIT.init(root);
  }

  // bind global delegates once
  MTM_ANCHOR_DROPDOWN.bindGlobalHandlers();
  MTM_KLAVIYO_DROPDOWN.bind();

  onReady(() => boot(document));

  // Shopify Theme Editor: re-init when a section reloads
  document.addEventListener("shopify:section:load", (e) => {
    if (e?.target) boot(e.target);
  });


  (() => {
  "use strict";

  function formatMoney(cents, moneyFormat) {
    const format = moneyFormat || window.theme?.moneyFormat || "${{amount}}";
    const value = (cents / 100).toFixed(2);

    return format.replace("{{amount}}", value)
      .replace("{{amount_no_decimals}}", Math.round(cents / 100))
      .replace("{{amount_with_comma_separator}}", value.replace(".", ","))
      .replace("{{amount_no_decimals_with_comma_separator}}", Math.round(cents / 100));
  }

  function updateFreeShippingBars(cart) {
    const bars = document.querySelectorAll("[data-free-shipping-limit]");

    bars.forEach((wrapper) => {
      const limit = parseFloat(wrapper.dataset.freeShippingLimit || 0);
      const messageTemplate = wrapper.dataset.freeShippingMessageTemplate || "You are ||amount|| away from free shipping";

      if (!limit) return;

      const subtotal = (cart.total_price || 0) / 100;
      const remainingCents = Math.max(0, Math.round(limit * 100) - (cart.total_price || 0));
      const percent = Math.max(0, Math.min(100, (subtotal / limit) * 100));
      const isQualified = subtotal >= limit;

      const progressBar = wrapper.querySelector("[data-progress-bar]");
      const leftToSpend = wrapper.querySelector("[data-left-to-spend]");
      const progressText = wrapper.querySelector(".free-shipping__progress-text");
      const defaultMessage = wrapper.querySelector(".free-shipping__default-message");
      const successMessage = wrapper.querySelector(".free-shipping__success-message");
      const graph = wrapper.querySelector("[data-progress-graph]");
      const graphProgress = wrapper.querySelector(".free-shipping__graph-progress");

      if (progressBar) {
        progressBar.value = percent;
      }

      if (graph && graphProgress) {
        const radius = 7;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference - (percent / 100) * circumference;
        graph.style.setProperty("--stroke-dashoffset", offset);
        graphProgress.style.strokeDasharray = `${circumference} ${circumference}`;
        graphProgress.style.strokeDashoffset = offset;
      }

      const moneyString = formatMoney(remainingCents, window.Shopify?.money_format);

      const rebuiltMessage = messageTemplate.replace(
        "||amount||",
        `<span data-left-to-spend>${moneyString}</span>`
      );

      if (progressText) {
        progressText.innerHTML = isQualified
          ? "Congrats you have free shipping!"
          : rebuiltMessage;
      }

      if (defaultMessage) {
        defaultMessage.innerHTML = rebuiltMessage;
      }

      if (successMessage) {
        successMessage.style.display = isQualified ? "" : "none";
      }

      if (defaultMessage) {
        defaultMessage.style.display = isQualified ? "none" : "";
      }

      wrapper.classList.toggle("is-success", isQualified);
    });
  }

  async function refreshFreeShipping() {
    try {
      const res = await fetch("/cart.js");
      const cart = await res.json();
      updateFreeShippingBars(cart);
    } catch (error) {
      console.error("Failed to refresh free shipping bar:", error);
    }
  }

  document.addEventListener("DOMContentLoaded", refreshFreeShipping);

  document.addEventListener("cart:updated", refreshFreeShipping);
  document.addEventListener("cart:refresh", refreshFreeShipping);
  document.addEventListener("theme:cart:change", refreshFreeShipping);

  document.addEventListener("click", (event) => {
    const removeBtn = event.target.closest(
      '[href*="/cart/change"], [data-remove], .cart__remove, .js-qty__adjust--minus'
    );

    if (removeBtn) {
      setTimeout(refreshFreeShipping, 300);
    }
  });
})();

})();



